import type { ReactNode } from "react";
import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { useNavigate } from "react-router";
import { type MessageKey, useT } from "../shell/i18n";
import { NotificationCenter } from "../ui/notification-center";
import { OnceOpened } from "../ui/pane";
import { PersonalizationChrome, usePersonalizationController } from "../ui/personalization";
import type { LeaveFormDraft } from "./ai-form-draft";
import { type Creation, isPaneCreation, type PaneCreation } from "./creations";
import { Palette } from "./palette";
import { HelpPane, SettingsPane } from "./shell-tools";

const AiChatPane = lazy(() =>
  import("./ai-chat").then((module) => ({ default: module.AiChatPane })),
);

interface ChatPanelState {
  readonly open: boolean;
  readonly paired: boolean;
  readonly activeCreation?: string;
  readonly onOpenChange: (open: boolean) => void;
  readonly onCreate: (id: string) => void;
  readonly onCreateDraft: (id: string, draft: LeaveFormDraft) => void;
  readonly canCreate: (id: string) => boolean;
}

const ChatPanel = createContext<ChatPanelState | null>(null);

const PAIR_QUERY = "(min-width: 80rem)";
function pairIsWide() {
  return typeof window !== "undefined" && (window.matchMedia?.(PAIR_QUERY).matches ?? false);
}
function watchPairWidth(changed: () => void) {
  const query = window.matchMedia?.(PAIR_QUERY);
  query?.addEventListener("change", changed);
  return () => query?.removeEventListener("change", changed);
}

/** A shell column beside the utility rail. Its chat stays mounted while forms open. */
export function ChatDock() {
  const state = useContext(ChatPanel);
  const t = useT();
  if (!state) return null;
  return (
    <aside
      aria-label={t("ai.chat.title")}
      aria-hidden={!state.open}
      inert={!state.open}
      data-open={state.open}
      className="chat-dock relative min-h-0 overflow-hidden bg-surface-panel"
      style={{ gridArea: "chat" }}
    >
      <OnceOpened open={state.open}>
        <Suspense fallback={null}>
          <AiChatPane
            embedded
            open={state.open}
            onOpenChange={state.onOpenChange}
            onCreate={state.onCreate}
            onCreateDraft={state.onCreateDraft}
            canCreate={state.canCreate}
            activeCreation={state.activeCreation}
            companion={state.paired}
          />
        </Suspense>
      </OnceOpened>
    </aside>
  );
}

/**
 * WHAT THE SHELL'S CONTROLS DO, published APART from the state they move.
 *
 * The topbar `+` and the search button sit in the frame; the pane and the palette they
 * raise sit outside it. Before this existed, both ends were one `useState` in one
 * component, which is the whole of the defect measured in
 * [every exit started slow](../../../docs/2026-08-09-every-exit-started-slow.md): opening
 * anything re-rendered the page underneath it, because the page and the pane were the same
 * render.
 *
 * So the STATE lives in `Overlays` and the INTENT travels down through here. The value is
 * two stable callbacks and holds no pane state at all — which is the load-bearing half: a
 * context carrying `creating` beside `create` would re-render every consumer on every
 * open, and the consumers are the frame. A provider whose value moves is a provider that
 * re-renders the tree under it, which is the shape this file is being rescued from.
 */
interface ShellActions {
  /** Open the command palette. */
  readonly search: () => void;
  /** Open the create pane for a creation, by its `what`. */
  readonly create: (id: string, within?: Readonly<Record<string, string>>) => void;
  /** Whether the current session has the licensed, authorized create flow. */
  readonly canCreate: (id: string) => boolean;
  /** Open the reader's working preferences. */
  readonly settings: () => void;
  /** Enter contextual editing mode for the page currently underneath the shell. */
  readonly personalize: () => void;
  /** Open product help without leaving the work underneath. */
  readonly help: () => void;
  readonly chat: () => void;
}

const Actions = createContext<ShellActions>({
  search: () => undefined,
  create: () => undefined,
  canCreate: () => false,
  settings: () => undefined,
  personalize: () => undefined,
  help: () => undefined,
  chat: () => undefined,
});

export function useShellActions(): ShellActions {
  return useContext(Actions);
}
/**
 * WHAT IS OPEN OVER THE PAGE, and the state that says so.
 *
 * The palette, every declared create flow and the toaster — all of them outside the grid,
 * for the reason the toaster has always been outside it, and all of them re-rendering
 * together on every open and close. That is the point: this is the component that is
 * ALLOWED to re-render when a pane opens, and the frame is not.
 *
 * `children` is the frame, handed through untouched. `Actions` publishes the two verbs and
 * never the two pieces of state, so a control in the topbar can raise a pane without
 * subscribing to whether one is open.
 */
export function Overlays({
  destinations,
  creations,
  aiEnabled,
  personId,
  roles,
  children,
}: {
  readonly destinations: readonly { to: string; label: MessageKey; group: MessageKey }[];
  readonly creations: readonly Creation[];
  readonly aiEnabled: boolean;
  /** The signed-in person, or `null` when this principal is nobody's employment record. */
  readonly personId: string | null;
  readonly roles: readonly string[];
  readonly children: ReactNode;
}) {
  const [searching, setSearching] = useState(false);
  const [setting, setSetting] = useState(false);
  const [helping, setHelping] = useState(false);
  const [chatting, setChatting] = useState(false);
  // THE CREATE PANE IS THE SHELL'S, and it opens OVER whatever is in view.
  //
  // This used to navigate to the screen that owned the flow and ask its pane to open
  // there. That was wrong, and Arda said so on sight: the page changes. A create flow
  // lives in a pane precisely so the page underneath survives it, and a control that
  // navigates has turned the flow into a destination — the thing somebody was reading is
  // gone before they have typed a character, and `useDiscardGuard` exists to guard a
  // half-filled form against exactly that navigation.
  //
  // So the address bar does not move. `creating` holds the chosen creation and its
  // `opens` is mounted below, beside the palette and the toaster rather than inside the
  // grid — see the note there about what happens to a region that mounts in the frame.
  //
  // AND THE PAGE UNDERNEATH SURVIVED IT IN EVERY SENSE BUT ONE: it was re-rendered from
  // scratch on the press. That is what moving this state out of `Shell` closes, and it is
  // why the measurement that found it was taken on a create flow — the one pane that
  // navigates nowhere, so nothing about routing could be blamed for the stall.
  const [creating, setCreating] = useState<{
    readonly creation: PaneCreation;
    readonly within?: Readonly<Record<string, string>>;
    readonly assistantDraft?: LeaveFormDraft;
    readonly fromChat?: boolean;
  } | null>(null);
  const pairIsWideNow = useSyncExternalStore(watchPairWidth, pairIsWide, () => false);
  const paired = chatting && creating !== null && pairIsWideNow;
  const navigate = useNavigate();
  const pagePersonalization = usePersonalizationController();
  const search = useCallback(() => setSearching(true), []);
  const settings = useCallback(() => setSetting(true), []);
  const personalize = pagePersonalization.start;
  const help = useCallback(() => setHelping(true), []);
  const chat = useCallback(() => {
    if (aiEnabled) setChatting(true);
  }, [aiEnabled]);
  // Keyed on `what`, which is a creation's identity the way `to` is a destination's. The
  // menu hands the identity back rather than the object, so nothing in `ui/` has to know
  // what a `Creation` is.
  const create = useCallback(
    (id: string, within?: Readonly<Record<string, string>>) => {
      const creation = creations.find((candidate) => candidate.what === id);
      if (!creation) {
        setCreating(null);
        return;
      }
      if (!isPaneCreation(creation)) {
        setCreating(null);
        setSearching(false);
        navigate(creation.to);
        return;
      }
      setCreating({ creation, within });
    },
    [creations, navigate],
  );
  const canCreate = useCallback(
    (id: string) => creations.some((candidate) => candidate.what === id),
    [creations],
  );
  const chatCreate = useCallback(
    (id: string) => {
      const chosen = creations.find((candidate) => candidate.what === id);
      if (chosen && isPaneCreation(chosen)) {
        setCreating({ creation: chosen, fromChat: true });
      }
    },
    [creations],
  );
  const chatCreateDraft = useCallback(
    (id: string, draft: LeaveFormDraft) => {
      const chosen = creations.find((candidate) => candidate.what === id);
      if (
        !["create.leaveRequest", "create.certifiedSickness"].includes(id) ||
        !chosen ||
        !isPaneCreation(chosen)
      )
        return;
      setCreating({ creation: chosen, assistantDraft: draft, fromChat: true });
    },
    [creations],
  );
  // STABLE ACROSS AN OPEN, which is the whole contract. Both callbacks are `useCallback`ed
  // on things that do not move while a pane is raised, so this value is `Object.is`-equal
  // from one open to the next and the frame's controls are never asked to render again.
  const actions = useMemo(
    () => ({ search, create, canCreate, settings, personalize, help, chat }),
    [search, create, canCreate, settings, personalize, help, chat],
  );
  const chatPanel = useMemo<ChatPanelState>(
    () => ({
      open: chatting,
      paired,
      activeCreation: creating?.creation.what,
      onOpenChange: setChatting,
      onCreate: chatCreate,
      onCreateDraft: chatCreateDraft,
      canCreate,
    }),
    [chatting, paired, creating?.creation.what, chatCreate, chatCreateDraft, canCreate],
  );

  return (
    <Actions.Provider value={actions}>
      <Palette
        open={searching}
        onOpenChange={setSearching}
        destinations={destinations}
        creations={creations.map((creation) => ({ id: creation.what, label: creation.what }))}
        onCreate={create}
      />
      <SettingsPane
        open={setting}
        onOpenChange={setSetting}
        onPersonalize={() => {
          setSetting(false);
          personalize();
        }}
      />
      <HelpPane open={helping} onOpenChange={setHelping} />
      <PersonalizationChrome />

      {/* EVERY DECLARED CREATE PANE, over whatever screen is in view and outside the
          grid for the same reason the toaster is. Nothing navigated to get here.

          ONE MOUNT PER FLOW FOR THE WHOLE SESSION, which is what `create-flows.tsx` and
          `expense-creates.tsx` both already say the shell does — "the shell mounts every
          declared flow at `open={false}`", which is the premise their fetch-on-open-and-
          keep-the-answer effects are written against. This used to render only the
          CHOSEN one, so closing a pane set `creating` to null and tore the flow out
          entirely: `usePrerequisites`' `held.ready` guard could never fire, and opening
          "Record a spend" a second time re-asked riksu all five of its prerequisites.
          Measured before this changed: five reads on the first open and the same five on
          the second, from a component that had been rebuilt from nothing in between.
          It also cost the exit transition, for the same reason `OnceOpened` exists.

          `OnceOpened` is what keeps that from becoming six flows fetching on every page
          load: a flow nobody has pressed is not mounted at all, and one that has been
          pressed stays. So the declaration in `creations.ts` is still the whole cost of
          adding a creation, and it still costs nothing until somebody opens it. */}
      {creations.filter(isPaneCreation).map((creation) => (
        <OnceOpened key={creation.what} open={creating?.creation.what === creation.what}>
          {/* A declaration is a lazy component: opening one flow must not download every
              other form in the menu. The existing page remains the complete fallback and
              the pane enters only when its module is ready, so a fast import cannot flash a
              loading card over otherwise usable work. `OnceOpened` still owns lifetime after
              the first open; Suspense changes only when the code first enters that lifetime. */}
          <Suspense fallback={null}>
            <creation.opens
              open={creating?.creation.what === creation.what}
              onOpenChange={(next) => !next && setCreating(null)}
              // WHO IS PRESSING. Every declared flow is offered it and most ignore it — a
              // job, a workplace and a payroll period are nobody's in particular. The one
              // that needs it is a request made in somebody's NAME, and the shell is the
              // only mount that can answer.
              personId={personId}
              roles={roles}
              within={creating?.creation.what === creation.what ? creating.within : undefined}
              assistantDraft={
                creating?.creation.what === creation.what ? creating.assistantDraft : undefined
              }
              companion={creating?.creation.what === creation.what && paired}
            />
          </Suspense>
        </OnceOpened>
      ))}

      <NotificationCenter />

      <ChatPanel.Provider value={chatPanel}>{children}</ChatPanel.Provider>
    </Actions.Provider>
  );
}
